/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { AppRoutingModule } from '@nibtravel/public/feature/router';
import {
  CONTENTFUL_CONFIG,
  ContentfulDataModule,
} from '@nibtravel/shared/data-access/contentful';
import { CountriesModule } from '@nibtravel/shared/data-access/countries';
import { GtmAnalyticsModule } from '@nibtravel/shared/data-access/gtm-analytics';
import {
  TealiumActionhandler,
  TealiumAnalyticsModule,
} from '@nibtravel/shared/data-access/tealium-analytics';
import { QuoteConfigModule } from '@nibtravel/shared/data-access/quote-config';
import {
  BrandContentResolver,
  UiContentModule,
} from '@nibtravel/shared/data-access/ui-content';
import {
  UnderwriterContentResolver,
  UnderwriterModule,
} from '@nibtravel/shared/data-access/underwriter';
import { ContentfulModule } from '@nibtravel/shared/features/contentful';
import { LayoutModule } from '@nibtravel/shared/ui/components/layout';
import { IconsModule } from '@nibtravel/shared/ui/icons';
import { ThemeModule } from '@nibtravel/shared/ui/theme';
import { APP_THEMES } from '@nibtravel/shared/ui/theme/app-themes';
import {
  AppConfigModule,
  AppInitializerService,
} from '@nibtravel/shared/util/app-config';
import { AppLoaderModule } from '@nibtravel/shared/util/app-loader';
import { Resolver } from '@nibtravel/shared/util/constants';
import { FeatureToggleModule } from '@nibtravel/shared/util/feature-toggle';
import { GtmModule } from '@nibtravel/shared/util/gtm';
import { i18nModule } from '@nibtravel/shared/util/i18n';
import { LoggerModule } from '@nibtravel/shared/util/logger';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import {
  ContentfulFactory,
  PublicAppFactory,
  PublicAppInitializerService,
} from './app.initializer';
import { PublicGtmProvider } from './gtm/public.gtm.provider';
import { GA4ActionHandler } from '../../../../libs/shared/data-access/gtm-analytics/src/lib/services/GA4ActionHandler';
import { NewrelicAnalyticsModule } from '@nibtravel/shared/data-access/newrelic-analytics';
import { NewrelicActionhandler } from '../../../../libs/shared/data-access/newrelic-analytics/src/lib/newrelic.actionhandler';

const sessionStorageStateObjects = [
  'quote',
  'quoteConfig',
  'underwriter',
  'plans',
  'countries',
];
let storagePluginOpt = {
  storage: StorageOption.SessionStorage,
};

// On dev mode, we need all state objects to be on sessionStorage, but only few on prod
if (environment.production) {
  storagePluginOpt = {
    ...storagePluginOpt,
    ...{ keys: sessionStorageStateObjects },
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppConfigModule.forRoot(environment),
    AppRoutingModule.forRoot(environment.production),
    BrowserAnimationsModule,
    BrowserModule,
    ContentfulDataModule,
    ContentfulModule.forRoot(),
    CountriesModule,
    FeatureToggleModule.forRoot(),
    GtmAnalyticsModule,
    GtmModule.forRoot(PublicGtmProvider),
    TealiumAnalyticsModule,
    NewrelicAnalyticsModule,
    HttpClientModule,
    IconsModule,
    LayoutModule,
    LoggerModule.forRoot(),
    NgxsModule.forRoot([], { developmentMode: !environment.production }),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    NgxsStoragePluginModule.forRoot(storagePluginOpt),
    QuoteConfigModule,
    ThemeModule.forRoot({ themes: APP_THEMES }),
    UiContentModule,
    UnderwriterModule,
    AppLoaderModule,
    i18nModule.forRoot({
      defaultLang: 'en',
      availableLangs: ['en', 'pt-br'],
      fallbackLang: 'en',
      resolvers: [
        {
          key: Resolver.BrandContent,
          resolver: BrandContentResolver,
        },
        {
          key: Resolver.UnderwriterContent,
          resolver: UnderwriterContentResolver,
        },
      ],
    }),
  ],
  providers: [
    PublicAppInitializerService,
    {
      provide: APP_INITIALIZER,
      useFactory: PublicAppFactory,
      deps: [
        AppInitializerService,
        PublicAppInitializerService,
        TealiumActionhandler,
        GA4ActionHandler,
        NewrelicActionhandler,
      ],
      multi: true,
    },
    {
      provide: CONTENTFUL_CONFIG,
      useFactory: ContentfulFactory,
      deps: [PublicAppInitializerService],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
